/**
 * useStageFooterDraft — manages draft state for the stage-footer calculations
 * editor. Mirrors the small surface of `useCardFieldsDraft` (draft, dirty,
 * get/reset/replace) plus per-calculation mutation handlers.
 *
 * A parallel `calcIds` array of stable UUIDs is maintained so list rows can use
 * stable React keys (never array indices).
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import type {
  PipelineFooterConfig,
  StageFooterFunction,
} from '../utils/stageFooterConfig';

export interface StageFooterDraftResult {
  draft: PipelineFooterConfig;
  /** Stable per-calculation ids, parallel to `draft.calculations`. */
  calcIds: string[];
  isDirty: boolean;
  getDraft: () => PipelineFooterConfig;
  /** Replace the draft AND reset the dirty baseline to the new config. */
  resetDraft: (next: PipelineFooterConfig) => void;
  /** Replace the draft WITHOUT moving the dirty baseline. */
  replaceDraft: (next: PipelineFooterConfig) => void;
  addCalculation: () => void;
  removeCalculation: (index: number) => void;
  setCalculationFunction: (index: number, fn: StageFooterFunction) => void;
  setCalculationProperty: (index: number, property: string) => void;
}

export function useStageFooterDraft(
  initialConfig: PipelineFooterConfig
): StageFooterDraftResult {
  const [draft, setDraftState] = useState<PipelineFooterConfig>(
    () => initialConfig
  );
  const draftRef = useRef(draft);

  const initialJson = useRef(JSON.stringify(initialConfig));

  const [calcIds, setCalcIds] = useState<string[]>(() =>
    initialConfig.calculations.map(() => crypto.randomUUID())
  );
  const calcIdsRef = useRef(calcIds);
  calcIdsRef.current = calcIds;

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== initialJson.current,
    [draft]
  );

  // Update both ref (synchronous, for sequential handler calls) and state.
  const commit = useCallback((next: PipelineFooterConfig) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);

  const addCalculation = useCallback(() => {
    const next: PipelineFooterConfig = {
      calculations: [...draftRef.current.calculations, { function: 'count' }],
    };
    setCalcIds(ids => [...ids, crypto.randomUUID()]);
    commit(next);
  }, [commit]);

  const removeCalculation = useCallback(
    (index: number) => {
      const next: PipelineFooterConfig = {
        calculations: draftRef.current.calculations.filter(
          (_, i) => i !== index
        ),
      };
      setCalcIds(ids => ids.filter((_, i) => i !== index));
      commit(next);
    },
    [commit]
  );

  const setCalculationFunction = useCallback(
    (index: number, fn: StageFooterFunction) => {
      const next: PipelineFooterConfig = {
        calculations: draftRef.current.calculations.map((calc, i) => {
          if (i !== index) {
            return calc;
          }
          // `count` carries no property — drop it when switching to count.
          if (fn === 'count') {
            return { function: 'count' };
          }
          return { ...calc, function: fn };
        }),
      };
      commit(next);
    },
    [commit]
  );

  const setCalculationProperty = useCallback(
    (index: number, property: string) => {
      const next: PipelineFooterConfig = {
        calculations: draftRef.current.calculations.map((calc, i) =>
          i === index ? { ...calc, property } : calc
        ),
      };
      commit(next);
    },
    [commit]
  );

  const resetDraft = useCallback((next: PipelineFooterConfig) => {
    draftRef.current = next;
    initialJson.current = JSON.stringify(next);
    setDraftState(next);
    setCalcIds(next.calculations.map(() => crypto.randomUUID()));
  }, []);

  const replaceDraft = useCallback((next: PipelineFooterConfig) => {
    draftRef.current = next;
    setDraftState(next);
    setCalcIds(next.calculations.map(() => crypto.randomUUID()));
  }, []);

  const getDraft = useCallback(() => draftRef.current, []);

  return useMemo(
    () => ({
      draft,
      calcIds,
      isDirty,
      getDraft,
      resetDraft,
      replaceDraft,
      addCalculation,
      removeCalculation,
      setCalculationFunction,
      setCalculationProperty,
    }),
    [
      draft,
      calcIds,
      isDirty,
      getDraft,
      resetDraft,
      replaceDraft,
      addCalculation,
      removeCalculation,
      setCalculationFunction,
      setCalculationProperty,
    ]
  );
}
